import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckSquare, Bell, Plus, Check, Trash2, X, Clock, Loader2, Edit3, Save, AlertCircle, Circle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { haptic } from '../../telegram';
import { tasksApi } from '../../api/tasksApi';
import { remindersApi } from '../../api/remindersApi';
import { agentsApi } from '../../api/agentsApi';
import type { Task, Reminder, Agent } from '../../types/contracts';

type Tab = 'tasks' | 'reminders';

function fmtDate(s: string) {
  try { return new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}
function toLocalInput(s?: string | null) {
  if (!s) return '';
  try { const d = new Date(s); const off = d.getTimezoneOffset(); const local = new Date(d.getTime() - off * 60000); return local.toISOString().slice(0, 16); } catch { return ''; }
}

export function TasksReminders() {
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('tasks');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [doneId, setDoneId] = useState<string | null>(null);
  const [fText, setFText] = useState('');
  const [fWhen, setFWhen] = useState('');
  const [fAgent, setFAgent] = useState('');
  const [fRepeat, setFRepeat] = useState('');

  const load = () => {
    Promise.all([tasksApi.list().catch(() => [] as Task[]), remindersApi.list().catch(() => [] as Reminder[]), agentsApi.list().catch(() => [] as Agent[])])
      .then(([t, r, a]) => { setTasks(Array.isArray(t) ? t : []); setReminders(Array.isArray(r) ? r : []); setAgents(Array.isArray(a) ? a : []); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const startAdd = () => { haptic('light'); setAdding(true); setEditingId(null); setFText(''); setFWhen(''); setFAgent(''); setFRepeat(''); setError(''); };

  const submit = async () => {
    if (!fText.trim() || !fWhen) { setError('Введите текст и дату'); return; }
    haptic('medium'); setSaving(true); setError('');
    const when = new Date(fWhen).toISOString();
    const repeat = fRepeat ? parseInt(fRepeat, 10) : undefined;
    try {
      if (tab === 'tasks') {
        if (editingId) { await tasksApi.update(editingId, { input: fText.trim(), when, repeatEveryMin: repeat }); }
        else { await tasksApi.create({ input: fText.trim(), when, repeatEveryMin: repeat, agentId: fAgent || undefined }); }
      } else {
        if (editingId) { await remindersApi.update(editingId, { text: fText.trim(), when, repeatEveryMin: repeat }); }
        else { await remindersApi.create({ text: fText.trim(), when, repeatEveryMin: repeat }); }
      }
      setAdding(false); setEditingId(null); haptic('heavy'); load();
    } catch (e: any) { setError(e?.message || 'Ошибка'); } finally { setSaving(false); }
  };

  const completeTask = async (id: string) => { haptic('medium'); try { await tasksApi.complete(id); setTasks((p) => p.filter((t) => t.id !== id)); } catch {} };
  const cancelTask = async (id: string) => { haptic('medium'); try { await tasksApi.cancel(id); setTasks((p) => p.filter((t) => t.id !== id)); } catch {} };
  const delTask = async (id: string) => { haptic('heavy'); try { await tasksApi.remove(id); setTasks((p) => p.filter((t) => t.id !== id)); } catch {} };
  const doneReminder = async (id: string) => {
    haptic('medium'); setDoneId(id);
    try { await remindersApi.done(id); setTimeout(() => setReminders((p) => p.filter((r) => r.id !== id)), 280); }
    catch { setDoneId(null); }
  };
  const delReminder = async (id: string) => { haptic('heavy'); try { await remindersApi.remove(id); setReminders((p) => p.filter((r) => r.id !== id)); } catch {} };

  const startEdit = (item: Task | Reminder, kind: Tab) => {
    haptic('light'); setTab(kind); setAdding(true); setEditingId(item.id);
    if (kind === 'tasks') { const t = item as Task; setFText(t.title || ''); setFWhen(toLocalInput(t.dueAt)); setFAgent(t.agentId || ''); setFRepeat(t.repeatEveryMin ? String(t.repeatEveryMin) : ''); }
    else { const r = item as Reminder; setFText(r.text || ''); setFWhen(toLocalInput(r.dueAt)); setFRepeat(r.repeatEveryMin ? String(r.repeatEveryMin) : ''); }
  };

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
        <div className="rf-topbar-title">Задачи и напоминания</div>
        {!adding && <button className="rf-back" onClick={startAdd}><Plus />Добавить</button>}
      </div>
      <div className="rf-detail-scroll">
        <div className="rf-info"><Clock /><div><b>Планировщик</b><br />Задачи — действия агента по расписанию. Напоминания — сообщения вам.</div></div>
        <div className="rf-seg">
          <button className={'rf-seg-btn' + (tab === 'tasks' ? ' active' : '')} onClick={() => { haptic('light'); setTab('tasks'); setAdding(false); setEditingId(null); }}><CheckSquare />Задачи</button>
          <button className={'rf-seg-btn' + (tab === 'reminders' ? ' active' : '')} onClick={() => { haptic('light'); setTab('reminders'); setAdding(false); setEditingId(null); }}><Bell />Напоминания</button>
        </div>

        {adding && (
          <div className="rf-card rf-acard rf-mb12">
            <div className="rf-field">
              <label>{tab === 'tasks' ? 'Что сделать' : 'Текст напоминания'}</label>
              <textarea className="rf-textarea" value={fText} onChange={(e) => setFText(e.target.value)} placeholder={tab === 'tasks' ? 'Например: Проверить почту' : 'Например: Позвонить маме'} rows={3} />
            </div>
            <div className="rf-field"><label>Когда</label><input className="rf-input" type="datetime-local" value={fWhen} onChange={(e) => setFWhen(e.target.value)} /></div>
            {tab === 'tasks' && !editingId && (
              <div className="rf-field"><label>Агент (необязательно)</label>
                <select className="rf-select" value={fAgent} onChange={(e) => setFAgent(e.target.value)}>
                  <option value="">Оркестратор (по умолчанию)</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
            <div className="rf-field"><label>Повторять каждые N минут (необязательно)</label><input className="rf-input" type="number" value={fRepeat} onChange={(e) => setFRepeat(e.target.value)} placeholder="Например: 1440 (раз в сутки)" /></div>
            {error && <div className="rf-result err"><AlertCircle /><div>{error}</div></div>}
            <div className="rf-btn-row">
              <button className="rf-btn primary" onClick={submit} disabled={saving || !fText.trim() || !fWhen}>{saving ? <Loader2 className="spin" /> : editingId ? <Save /> : <Plus />}{editingId ? 'Сохранить' : 'Создать'}</button>
              <button className="rf-btn" onClick={() => { setAdding(false); setEditingId(null); }}><X />Отмена</button>
            </div>
          </div>
        )}

        {!adding && loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : !adding && tab === 'tasks' ? (
          tasks.length === 0 ? (
            <div className="rf-empty"><div className="rf-empty-ic"><CheckSquare /></div><h3>Задач нет</h3><p>Создайте задачу кнопкой «Добавить»</p></div>
          ) : (
            <div className="rf-rows rf-stagger">
              {tasks.map((t) => (
                <div key={t.id} className="rf-card rf-acard">
                  <div className="rf-conn-row">
                    <div className="rf-row-ic"><CheckSquare /></div>
                    <div className="rf-conn-info">
                      <div className="rf-conn-name">{t.title}</div>
                      <div className="rf-conn-sub"><Clock />{fmtDate(t.dueAt || '')}{t.repeatEveryMin ? ' · повтор ' + t.repeatEveryMin + ' мин' : ''}{t.agentId ? ' · агент' : ''}</div>
                    </div>
                  </div>
                  <div className="rf-acts">
                    <button className="rf-btn sm primary rf-grow" onClick={() => completeTask(t.id)}><Check />Выполнить</button>
                    <button className="rf-btn sm icon" title="Изменить" onClick={() => startEdit(t, 'tasks')}><Edit3 /></button>
                    <button className="rf-btn sm icon" title="Отменить" onClick={() => cancelTask(t.id)}><X /></button>
                    <button className="rf-btn sm danger icon" title="Удалить" onClick={() => delTask(t.id)}><Trash2 /></button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : !adding && tab === 'reminders' ? (
          reminders.length === 0 ? (
            <div className="rf-empty"><div className="rf-empty-ic"><Bell /></div><h3>Напоминаний нет</h3><p>Создайте напоминание кнопкой «Добавить»</p></div>
          ) : (
            <div className="rf-rows rf-stagger">
              {reminders.map((r) => {
                const done = doneId === r.id;
                return (
                  <div key={r.id} className={'rf-card rf-acard rf-remind' + (done ? ' done' : '')}>
                    <div className="rf-conn-row">
                      <button className={'rf-check' + (done ? ' on' : '')} onClick={() => doneReminder(r.id)} aria-label="Отметить выполненным">{done ? <Check /> : <Circle />}</button>
                      <div className="rf-conn-info">
                        <div className="rf-conn-name">{r.text}</div>
                        <div className="rf-conn-sub"><Clock />{fmtDate(r.dueAt || '')}{r.repeatEveryMin ? ' · повтор ' + r.repeatEveryMin + ' мин' : ''}</div>
                      </div>
                    </div>
                    <div className="rf-acts">
                      <button className={'rf-btn sm rf-grow' + (done ? ' primary' : '')} onClick={() => doneReminder(r.id)}><Check />{done ? 'Выполнено' : 'Выполнена'}</button>
                      <button className="rf-btn sm icon" title="Изменить" onClick={() => startEdit(r, 'reminders')}><Edit3 /></button>
                      <button className="rf-btn sm danger icon" title="Удалить" onClick={() => delReminder(r.id)}><Trash2 /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
